import * as helpers from '../helpers';
import StratisElementAbstract from './stratis-element.abstract';
import StratisFactory from './stratis-factory';

/**
 * Class for creating sticky elements.
 */
export default class FixedElement extends StratisElementAbstract {
    constructor(selector = '.js-fixed-element', options = {}) {
        super(selector);

        this.options = {
            addPlaceholderElement: true,
            mode: 'down', // up, down, down-bottom, offset
            offset: 0,
            stickDelay: 300,
            classList: {
                onPlaceholderRender: 'js-fixed-el-abs',
                onScroll: 'js-fixed-el',
                onScrollUp: 'js-fixed-el-up',
                onScrollUpHide: 'js-fixed-el-up-hide',
                onScrollDown: 'js-fixed-el-down',
            },
            onInit: (instance) => {
            },
            onScroll: (instance) => {
            },
            onStick: (instance) => {
            },
            onUnstick: (instance) => {
            },
            onResize: (instance) => {
            },
        };

        this._placeholder = null;
        this.lastScrollPosition = 0;
        this.height = this.element.offsetHeight;
        this.elementYOffset = this.element.getBoundingClientRect().top;
        this.pageDetailPopup = document.querySelector('.page-detail');
        this.fixPoint = this.pageDetailPopup
            ? this.pageDetailPopup.scrollTop + this.elementYOffset
            : window.pageYOffset + this.elementYOffset;

        // Bindings
        this._onScrollHandler = this._onScroll.bind(this);
        this._onResizeHandler = this._onResize.bind(this);

        if (options instanceof Object) {
            this.setOptions(options);
        }

        this.checkBeforeInit();
    }

    /**
     * Scroll handler.
     */
    _onScroll() {
        let scrollPos = window.pageYOffset;

        if (this.pageDetailPopup) {
            scrollPos = this.pageDetailPopup.scrollTop;
        }

        const {
            mode,
            classList,
            onScroll,
            onStick,
            onUnstick,
        } = this.options;

        if (scrollPos >= this.fixPoint) {
            helpers.removeClass(this.element, classList.onPlaceholderRender);

            switch (mode) {
                case 'down':
                    helpers.addClass(this.element, [classList.onScroll, classList.onScrollDown]);
                    onStick(this);
                    break;
                case 'offset':
                case 'down-bottom':
                    helpers.addClass(this.element, classList.onScroll);

                    setTimeout(() => {
                        if (helpers.hasClass(this.element, classList.onScroll)) {
                            helpers.addClass(this.element, classList.onScrollDown);
                        }

                        onStick(this);
                    }, this.options.stickDelay);

                    break;
                case 'up':
                    if (scrollPos < this.lastScrollPosition) {
                        helpers.removeClass(this.element, classList.onScrollUpHide);
                        helpers.addClass(this.element, classList.onScroll);
                        helpers.addClass(this.element, classList.onScrollUp);
                        onStick(this);
                    } else {
                        helpers.addClass(this.element, classList.onScrollUpHide);
                        helpers.removeClass(this.element, classList.onScrollUp);
                    }

                    break;
                default:
                    helpers.addClass(this.element, classList.onScroll);
            }
        } else {
            helpers.removeClass(this.element, classList.onScroll);
            helpers.removeClass(this.element, classList.onScrollDown);
            helpers.removeClass(this.element, classList.onScrollUp);
            helpers.addClass(this.element, classList.onPlaceholderRender);
            onUnstick(this);
        }

        onScroll(this);
        setTimeout(() => {
            this.lastScrollPosition = scrollPos;
        }, 250);
    }

    /**
     * Resize handler
     */
    _onResize() {
        this.height = this.element.offsetHeight;

        if (this._placeholder) {
            this._placeholder.style.height = this.height;
        }

        this.options.onResize(this);
    }

    /**
     * Add event listeners.
     */
    _addHandlers() {
        window.addEventListener('scroll', this._onScrollHandler);
        window.addEventListener('resize', this._onResizeHandler);
        if (this.pageDetailPopup) {
            this.pageDetailPopup.addEventListener('scroll', this._onScrollHandler);
        }
    }

    /**
     * Remove all event listeners.
     */
    _removeHandlers() {
        window.removeEventListener('scroll', this._onScrollHandler);
        window.removeEventListener('resize', this._onResizeHandler);
        if (this.pageDetailPopup) {
            this.pageDetailPopup.removeEventListener('scroll', this._onScrollHandler);
        }
    }

    /**
     * Add empty placeholder element for substitution fixed element.
     */
    _addPlaceholderElement() {
        const placeholder = document.createElement('div');
        placeholder.style.height = `${this.height}px`;
        helpers.insertAfter(placeholder, this.element);
        this._placeholder = placeholder;
        helpers.addClass(this.element, this.options.classList.onPlaceholderRender);
    }

    checkBeforeInit() {
        if (typeof this.element !== 'string') {
            if (helpers.hasClass(this.element, '-is-initialized')) {
                return;
            }
            return this.element === null ? false : this.init();
        }
    }

    /**
     * Initialize functionallity.
     */
    init() {
        helpers.addClass(this.element, '-is-initialized');
        if (this.options.mode === 'offset') {
            this.fixPoint = this.options.offset;
        } else if (this.options.mode === 'down-bottom' || this.options.mode === 'up') {
            this.fixPoint += (this.height + this.options.offset);
        }

        if (this.options.addPlaceholderElement && this.getElementPosition() !== 'absolute' && this.getElementPosition() !== 'fixed') {
            this._addPlaceholderElement();
        }

        this._addHandlers();
        this.options.onInit(this);
    }

    /**
     * Get element position type.
     */
    getElementPosition() {
        return getComputedStyle(this.element).position;
    }

    /**
     * Destroy instance.
     */
    destroy() {
        this._removeHandlers();

        if (this.placeholder) {
            this.placeholder.parentElement.removeChild(this.placeholder);
        }
    }

    /**
     * Rebuild instance.
     */
    update() {
        this.destroy();
        this.init();
    }

    /**
     * Create class instances.
     * @param {string} selector - elements selector to search.
     * @param {object} options  - plugin options.
     */
    static create(selector, options) {
        return StratisFactory.create(selector, options, FixedElement);
    }
}
